import { useEffect, useMemo, useState } from "react";
import { useAppAuthState } from "../../hooks/useAppAuthState";
import { isPlaceLikedByUser, likePlace } from "../../services/likesService";
import { Place } from "../../types/Place";
import "./PlaceLikes.scss";

interface PlaceLikesProps {
  place: Place;
}

const PlaceLikes: React.FC<PlaceLikesProps> = ({ place }) => {
  const { user, isAuthLoading } = useAppAuthState();

  const [likes, setLikes] = useState<number>(0);
  const [isLiked, setIsLiked] = useState<boolean>(false);
  const [isLoadingLikeStatus, setIsLoadingLikeStatus] = useState<boolean>(true);
  const [isSubmittingLike, setIsSubmittingLike] = useState<boolean>(false);

  useEffect(() => {
    setLikes(place.likes || 0);
  }, [place]);

  useEffect(() => {
    const fetchLikeStatus = async () => {
      if (user && place?.googlePlaceId) {
        setIsLoadingLikeStatus(true);

        const userLikesPlace = await isPlaceLikedByUser(
          user.uid,
          place.googlePlaceId
        );
        setIsLiked(userLikesPlace);
        setIsLoadingLikeStatus(false);
      } else if (!user && !isAuthLoading) {
        setIsLoadingLikeStatus(false);
        setIsLiked(false);
      }
    };

    fetchLikeStatus();
  }, [user, place, isAuthLoading]);

  const handleLike = async () => {
    if (
      !user ||
      isLiked ||
      isLoadingLikeStatus ||
      isSubmittingLike ||
      !place?.googlePlaceId
    ) {
      return;
    }

    setIsSubmittingLike(true);

    // We'll swallow the error and toast it to the user.
    likePlace(user.uid, place.googlePlaceId, "toast")
      .then(() => {
        setIsLiked(true);
        setLikes((prevLikes) => prevLikes + 1);
      })
      .finally(() => {
        setIsSubmittingLike(false);
      });
  };

  const likeCountMessage = useMemo(() => {
    if (isLiked) {
      if (likes === 1) {
        return "You like this place.";
      }
      return `You and others (${likes - 1}) like this place.`;
    }
    if (likes === 0) {
      return "Be the first to like this place.";
    }
    if (likes === 1) {
      return `1 like.`;
    }
    return `${likes} likes.`;
  }, [likes, isLiked]);

  return (
    <div className="like-section">
      <button
        onClick={handleLike}
        disabled={isLoadingLikeStatus || isLiked || isSubmittingLike || !user}
        className="btn btn-primary btn-sm"
      >
        {isLoadingLikeStatus ? "Loading..." : isLiked ? "Liked" : "Like"}
      </button>
      <div className="like-count">{likeCountMessage}</div>
    </div>
  );
};

export default PlaceLikes;
